import { Icon } from "@mdi/react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiChevronDown } from "@mdi/js";
import { Popover } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import {
  ALL_WELL_STATUSES,
  EvaChart,
  wellStatusCurrentOptions,
  wellStatusOverTimeOptions
} from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import {
  resolveWellStatusSelection,
  wellStatusDisplayName
} from "entities/charts/eva/requests/wellStatus";
import { DEFAULT_WELL_STATUS_CURRENT_REQUEST } from "entities/charts/eva/requests/wellStatusCurrent";
import { defaultWellStatusOverTimeRequest } from "entities/charts/eva/requests/wellStatusOverTime";
import { CURRENT_VALUE_AXIS_TITLE_FONT_SIZE } from "entities/charts/eva/translators/wellStatusCurrent";
import { useAtomValue } from "jotai";
import { updateChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { ChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { visibilityToggleStyles } from "../../shared/SharedStyles";

/**
 * Status control for both Well Status charts.
 *
 * Mounted on the axis its status TITLES, in both charts:
 * - `yAxis` — Status Over Time, where the bars count one status over time;
 * - `xAxis` — Current Well Status, whose horizontal bars put that count on the
 *   x (value) axis instead.
 *
 * Either way it is a rotated/positioned button sitting ON the axis, overlaying
 * the axis title it edits and opening a popover list — the same pattern and
 * styling as the rig/frac metric selector, which is what the mockup mirrors.
 *
 * The two charts list DIFFERENT vocabularies and that is deliberate:
 * - over-time offers the statuses the server can DERIVE from date fields;
 * - current offers stored `header.status` values, which are stage-prefixed
 *   ("06. PROD") — so the option's value carries the prefix while the menu shows
 *   the bare abbreviation.
 */
interface WellStatusSelectorProps {
  placement: "yAxis" | "xAxis";
}

function WellStatusSelector({ placement }: WellStatusSelectorProps) {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request, chartType, options, screenshot, dataTable } = useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;
  const [open, setOpen] = useState(false);

  const isCurrent = chartType === EvaChart.WellStatusCurrent;
  const selections = request as unknown as Maybe<{
    status?: string;
    allStatuses?: boolean;
  }>;
  // Validated, not just defaulted-when-absent: a status carried over from the
  // other chart is not in `options`, so the button would label itself with a raw
  // value the menu cannot select. Must match the controller exactly or the
  // button and the request disagree.
  // ALL_WELL_STATUSES is a dropdown value, never a wire value — the request
  // carries the explicit `allStatuses` flag instead, so read the selection back
  // off that flag. This component is the ONLY place the literal is interpreted.
  const value =
    isCurrent && selections?.allStatuses
      ? ALL_WELL_STATUSES
      : resolveWellStatusSelection(selections?.status, isCurrent);

  // Both vocabularies are constants, and both render their labels through
  // wellStatusDisplayName so a name lives in one place: the VALUES stay the
  // strings the server parses ("06. DRAIN (PROD)"), the labels are what the menu
  // shows ("Drain Leg (Producing)").
  //
  // The current chart offers the whole vocabulary rather than only the statuses
  // present in the filter — picking one that matches nothing draws an empty chart,
  // which is a useful answer rather than a fault.
  const statusOptions = useMemo(
    () =>
      (isCurrent ? wellStatusCurrentOptions() : wellStatusOverTimeOptions()).map(
        (option) => ({ value: option, label: wellStatusDisplayName(option) })
      ),
    [isCurrent]
  );

  const label = statusOptions.find((o) => o.value === value)?.label ?? value;

  // The button stands in for the canvas axis title hidden under it, so it
  // shows that exact string ("DUC Count") rather than the bare status — the
  // server owns that wording (it also heads the tooltip), so read it live from
  // the persisted options instead of re-deriving the format here. The status
  // titles the y-axis on the over-time chart and the x (value) axis on the
  // horizontal current chart.
  const axisTitle = useMemo(() => {
    try {
      const parsed = JSON.parse(options || "{}");
      // Only trust axes this chart type produced. The JSON survives a chart-type
      // switch, and the two charts label the same axis differently — so without
      // this the Current chart rendered the over-time chart's "Time" on its
      // x-axis button until the first response for the new type landed.
      if (parsed?.chartType !== chartType) return label;
      const axis = placement === "xAxis" ? parsed?.xAxis : parsed?.yAxis;
      const name = (Array.isArray(axis) ? axis[0] : axis)?.name;
      return typeof name === "string" && name.length > 0 ? name : label;
    } catch {
      return label;
    }
  }, [options, placement, label, chartType]);

  /**
   * Horizontal offset (px, right of the container centre) for the `xAxis`
   * button, which overlays the value-axis title ECharts centres on the PLOT, not
   * the container. The current chart's `grid.left` adapts to the category-label
   * band while `grid.right` is fixed, so the title sits
   * (grid.left − grid.right) / 2 right of centre. Read live from the persisted
   * options so the button tracks the band as labels change length; 0 until the
   * options exist. Mirrors RigFracMetricSelector's `bottom` placement, which has
   * the same coupling.
   */
  const xAxisOffsetPx = useMemo(() => {
    if (placement !== "xAxis") return 0;
    try {
      const parsed = JSON.parse(options || "{}");
      // Same staleness guard as the title: the two charts have different grid
      // geometry, so the previous type's insets would place the button wrongly.
      if (parsed?.chartType !== chartType) return 0;
      const grid = parsed?.grid;
      const left = grid?.left;
      const right = grid?.right;
      if (typeof left !== "number" || typeof right !== "number") return 0;
      const offset = (left - right) / 2;
      return Number.isFinite(offset) ? offset : 0;
    } catch {
      return 0;
    }
  }, [options, placement, chartType]);

  const commit = (key: string) => {
    setOpen(false);
    if (key === value) return;
    const defaults = isCurrent
      ? DEFAULT_WELL_STATUS_CURRENT_REQUEST
      : defaultWellStatusOverTimeRequest();
    // Translate the dropdown's "All Statuses" entry into the flag here, so the
    // literal never reaches the request, chart-svc or eva-data-svc-rs.
    const allStatuses = isCurrent && key === ALL_WELL_STATUSES;
    dispatch(
      updateChart({
        id,
        request: {
          ...defaults,
          ...selections,
          status: allStatuses ? "" : key,
          ...(isCurrent ? { allStatuses } : {})
        } as unknown as ChartRequestType
      })
    );
  };

  const visible = isHover && !screenshot?.active && !dataTable?.active;
  const isXAxis = placement === "xAxis";

  return (
    <AxisWrapper
      $xOffset={xAxisOffsetPx}
      data-x-offset={xAxisOffsetPx}
      className={classnames(placement, { visible })}
      data-testid={`well-status-${isXAxis ? "x" : "y"}-axis-status`}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        // The y-axis button hugs the widget's left edge, so opening screen-left
        // lands the list over the map rather than covering the bars it is there
        // to re-filter (same rationale as the rig/frac left metric). The x-axis
        // button sits at the bottom, so its list opens upward.
        placement={isXAxis ? "top" : "left"}
        // antd reserves ~15.3px on the placement side for the (hidden) arrow;
        // pull the popup back so the list sits flush against the button.
        align={{ offset: isXAxis ? [0, 14] : [14, 0] }}
        overlayClassName="rig-frac-metric-popover"
        content={
          <PopoverContent role="menu">
            {/* Keyboard-operable: the list scrolls to 16 entries on the over-time
                chart, so mouse-only would strand most of it. Enter and Space both
                commit, which is what role="menuitem" promises. */}
            {statusOptions.map((option) => (
              <MenuItem
                key={option.value}
                role="menuitem"
                tabIndex={0}
                aria-checked={option.value === value}
                className={classnames({ isSelected: option.value === value })}
                data-testid={`well-status-option-${option.value}`}
                onClick={() => commit(option.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  // Space would otherwise scroll the popover it is inside.
                  event.preventDefault();
                  commit(option.value);
                }}>
                {option.label}
              </MenuItem>
            ))}
          </PopoverContent>
        }>
        <AxisButton
          data-testid={`well-status-${isXAxis ? "x" : "y"}-axis-status-trigger`}>
          <AxisLabel>{axisTitle}</AxisLabel>
          <Icon path={mdiChevronDown} size={0.75} />
        </AxisButton>
      </Popover>
    </AxisWrapper>
  );
}

export default WellStatusSelector;

const AxisWrapper = styled.div<{ $xOffset: number }>`
  ${visibilityToggleStyles};
  position: absolute;
  z-index: 1;
  transform-origin: center;

  /* Rotated to read vertically along the y-axis, mirroring the rig/frac metric
     selector's .left placement — the offsets are the ones measured there
     against a headless ECharts render, and the over-time chart shares that grid
     geometry (GRID_LEFT 66 / y-axis nameGap 46, GRID_TOP 46).
     NOTE: no backticks in these comments — this is a template literal, and one
     would terminate it mid-rule. */
  &.yAxis {
    top: calc(50% - 27px);
    left: 0;
    transform: translate(calc(-50% + 15px), -50%) rotate(-90deg);
  }

  /* Horizontal (un-rotated) button on the current chart's bottom value axis.
     Centred on the PLOT rather than the container: grid.left adapts to the
     category-label band while grid.right is fixed, so the title sits $xOffset
     right of centre (see xAxisOffsetPx). Without it the button reads visibly
     off-centre against the title behind it. */
  &.xAxis {
    bottom: 6px;
    left: calc(50% + ${(p) => p.$xOffset}px);
    transform: translateX(-50%);

    button {
      font-size: ${CURRENT_VALUE_AXIS_TITLE_FONT_SIZE}px;
    }
  }
`;

const AxisButton = styled.button`
  display: inline-flex;
  align-items: center;
  /* Hug the text so the button tracks the axis title it replaces on hover. */
  width: fit-content;
  max-width: 340px;
  justify-content: center;
  gap: 2px;
  padding: 2px 6px 2px 12px;
  border: 1px solid var(--color-surface-04);
  border-radius: var(--border-radius);
  background-color: var(--white);
  color: var(--color-text);
  font-weight: var(--fontWeightMedium);
  /* Same size as the echarts axis title it replaces (DEFAULT_AXIS_FONT_SIZE). */
  font-size: 15px;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary);
  }
`;

// Ellipsis lives on the label, not the button, so the chevron never clips.
const AxisLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 160px;
  /* 16 statuses is more than fits comfortably; scroll rather than run off the
     widget. */
  max-height: 320px;
  overflow-y: auto;
  /* Same trap as the chart tooltips (see WellStatusChart): the list is
     shrink-to-fit above min-width, and overflow-y:auto forces overflow-x to
     compute to "auto" — so the vertical scrollbar this list ALWAYS shows
     (16 statuses > 320px) takes its width out of the content box and a
     horizontal scrollbar appears under the options. */
  overflow-x: hidden;
  padding: 0;
  border-radius: var(--border-radius);
`;

const MenuItem = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  color: var(--color-text);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-text-06);
  }

  &.isSelected {
    background-color: var(--smoke);
    color: var(--color-text);
  }
`;
